import React, { useState, useEffect } from 'react';

import {
  Box, Button, Select, MenuItem, Checkbox, Typography,
  FormControl, InputLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import { columns } from './columns';
import {
  api,
  useGetAllRubricsByCampAndEventMutation,
  useAssignRubricsToJudgesMutation,
} from '../../../redux/services';
import { useEnqueueStacks } from '../../../hooks/use-enqueue-stacks';
import { useAuthContext } from '../../../context/auth-context';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const initialFormValues = {
  event: '-',
  juez: [],
  rubrics: [],
  isLoading: false,
};

export const AssignUserRubric = props => {
  const { handleClose } = props;
  const [formData, setFormData] = useState(initialFormValues);

  const { activeCamp } = useAuthContext();
  const { enqueueSuccess, enqueueError } = useEnqueueStacks();

  const [getUsersByCamp, { data: judges }] = api.endpoints.getUsersByCamp.useLazyQuery();
  const [getEventsByCamp, { data: events }] = api.endpoints.getEventsByCamp.useLazyQuery();
  const [rubricsByCampAndEvent, { data: rubrics }] = useGetAllRubricsByCampAndEventMutation();
  const [assignRubricsToJudges] = useAssignRubricsToJudgesMutation();

  // Auto-populate events and judges when component mounts
  useEffect(() => {
    if (activeCamp?._id) {
      getEventsByCamp(activeCamp._id);
      getUsersByCamp({ _id: activeCamp._id });
    }
  }, [activeCamp?._id]);

  const handleChange = async e => {
    const { name, value } = e.target;
    setFormData(curr => ({ ...curr, [name]: value }));

    if (name === 'event') {
      await rubricsByCampAndEvent({ campId: activeCamp._id, eventId: value });
    }
  };

  const handleSelectAllJudges = e => {
    if (e.target.checked) {
      setFormData(curr => ({ ...curr, juez: filteredJudges.map(j => j._id) }));
    } else {
      setFormData(curr => ({ ...curr, juez: [] }));
    }
  };

  const handleRubricSelection = params => {
    setFormData(curr => ({ ...curr, rubrics: params }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formData.juez.length || !formData.rubrics.length) {
      enqueueError('Selecciona al menos un juez y una rúbrica', {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    try {
      setFormData(curr => ({ ...curr, isLoading: true }));
      const res = await assignRubricsToJudges({
        rubrics: formData.rubrics,
        userIds: formData.juez,
      });

      if (!res.error) {
        enqueueSuccess('Rúbricas asignadas exitosamente', {
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        handleClose();
      } else {
        enqueueError('Error al asignar rúbricas', {
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    } catch (err) {
      console.error(err);
      enqueueError('Error inesperado al asignar rúbricas', {
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setFormData(curr => ({ ...curr, isLoading: false }));
    }
  };

  const filteredJudges = (judges || []).filter(j => j.role !== 'director');
  const allJudgesSelected = filteredJudges.length > 0 && formData.juez.length === filteredJudges.length;
  const someJudgesSelected = formData.juez.length > 0 && !allJudgesSelected;

  return (
    <Box component='form' sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Typography variant='subtitle1' fontWeight={700}>Asignar Juez a Rúbrica</Typography>

      {/* Selects — stack on mobile, side by side on desktop */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <FormControl fullWidth required>
          <InputLabel id='event-select-label'>Evento</InputLabel>
          <Select
            labelId='event-select-label'
            name='event'
            value={formData.event}
            onChange={handleChange}
            label='Evento'
          >
            <MenuItem value='-' disabled>Seleccione un evento</MenuItem>
            {(events || []).map(option => (
              <MenuItem key={option._id} value={option._id}>{option.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth required>
          <InputLabel id='juez-select-label' shrink>Jueces</InputLabel>
          <Select
            labelId='juez-select-label'
            name='juez'
            value={formData.juez}
            onChange={handleChange}
            label='Jueces'
            notched
            multiple
            displayEmpty
            renderValue={selected => {
              if (selected.length === 0) return 'Seleccione jueces';
              return selected.map(id => {
                const judge = filteredJudges.find(j => j._id === id);
                return judge ? judge.fullname : id;
              }).join(', ');
            }}
            MenuProps={{
              PaperProps: { style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP } },
            }}
            displayEmpty
          >
            <MenuItem
              dense
              onClick={e => {
                e.stopPropagation();
                handleSelectAllJudges({ target: { checked: !allJudgesSelected } });
              }}
              sx={{ borderBottom: '1px solid #eee' }}
            >
              <Checkbox
                checked={allJudgesSelected}
                indeterminate={someJudgesSelected}
                onChange={handleSelectAllJudges}
                onClick={e => e.stopPropagation()}
              />
              <Typography variant='body2' fontWeight={600}>Todos</Typography>
            </MenuItem>
            {filteredJudges.map(option => (
              <MenuItem key={option._id} value={option._id}>
                <Checkbox checked={formData.juez.indexOf(option._id) > -1} />
                {option.fullname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Rubrics DataGrid */}
      <Box sx={{ height: { xs: 280, sm: '40vh' }, width: '100%' }}>
        <DataGrid
          rows={rubrics ?? []}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          checkboxSelection
          onRowSelectionModelChange={handleRubricSelection}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          disabled={formData.isLoading}
          onClick={handleSubmit}
          variant='contained'
          type='submit'
          fullWidth={false}
          sx={{ minWidth: 120 }}
        >
          Guardar
        </Button>
      </Box>
    </Box>
  );
};
